import { REPOSITORY_TYPES } from './DynamicRepositoryNameField';

/** Bitbucket Cloud repository slug max length (API rejects longer slugs). */
export const MAX_BITBUCKET_REPO_SLUG_LENGTH = 62;

/** Backstage catalog metadata.name allows up to 63 (Kubernetes object name). */
export const MAX_CATALOG_ENTITY_NAME_LENGTH = 63;

/**
 * Effective limit for scaffolder repo names on every provider (tightest of
 * Bitbucket vs catalog), so a name is valid wherever the repository is created.
 */
export const MAX_REPOSITORY_NAME_LENGTH = MAX_BITBUCKET_REPO_SLUG_LENGTH;

export const repositoryNameTooLongError = (length: number) =>
  `Repository name is ${length} characters; maximum is ${MAX_REPOSITORY_NAME_LENGTH}.`;

// Helper function to validate repository name format (supports partial names)
export const validateRepositoryName = (
  value: string,
  expectedOwner?: string,
  expectedType?: string,
) => {
  if (!value) return { isValid: true, errors: [] }; // Empty is OK during progressive filling

  if (value.length > MAX_REPOSITORY_NAME_LENGTH) {
    return {
      isValid: false,
      errors: [repositoryNameTooLongError(value.length)],
    };
  }

  // Check if it's a partial name (owner only, owner-type, or full owner-type-purpose)
  const fullPattern = /^([a-z]+)-([a-z0-9]+)-([a-z0-9-]+)$/;
  const ownerTypePattern = /^([a-z]+)-([a-z0-9]+)$/;
  const ownerOnlyPattern = /^([a-z]+)$/;

  const errors: string[] = [];
  let owner = '';
  let type = '';

  // Try to match the different patterns
  const fullMatch = value.match(fullPattern);
  const ownerTypeMatch = value.match(ownerTypePattern);
  const ownerOnlyMatch = value.match(ownerOnlyPattern);

  if (fullMatch) {
    // Full format: owner-type-purpose
    [, owner, type] = fullMatch;
  } else if (ownerTypeMatch) {
    // Partial format: owner-type
    [, owner, type] = ownerTypeMatch;
  } else if (ownerOnlyMatch) {
    // Partial format: owner only
    [, owner] = ownerOnlyMatch;
  } else {
    // Invalid format
    return {
      isValid: false,
      errors: [
        'Repository name must follow format: owner-type-purpose (all lowercase, hyphens allowed)',
      ],
    };
  }

  // Validate owner prefix matches selected group
  if (expectedOwner && owner !== expectedOwner) {
    errors.push(
      `Owner prefix "${owner}" doesn't match your selected Product Area (expected: "${expectedOwner}")`,
    );
  }

  // Validate type matches selection (only if type is present and expected)
  if (type && expectedType && type !== expectedType) {
    errors.push(
      `Type "${type}" doesn't match your selected Repository Type (expected: "${expectedType}")`,
    );
  }

  // Validate against allowed types (only if type is present)
  if (type) {
    const allowedTypes = REPOSITORY_TYPES.map(rt => rt.value);

    if (!allowedTypes.includes(type)) {
      errors.push(
        `Type "${type}" is not allowed. Must be one of: ${allowedTypes.join(
          ', ',
        )}`,
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Helper function to get appropriate helper text based on repository name status
export const getHelperText = (
  finalRepositoryName: string,
  validationResult: { isValid: boolean; errors: string[] },
  ownerPrefix?: string,
  repositoryType?: string,
  repositoryPurpose?: string,
): string => {
  if (!finalRepositoryName) {
    return 'Repository name builds as you make selections.';
  }

  if (!validationResult.isValid) {
    return 'Repository name has validation errors - check field requirements above.';
  }

  if (ownerPrefix && repositoryType && repositoryPurpose) {
    return 'Repository name is valid and ready to use.';
  }

  return 'Repository name is building as you complete the fields.';
};

// Helper function to display repository name with status emoji
export const getDisplayValueWithStatus = (
  repositoryName: string,
  validationResult: { isValid: boolean; errors: string[] },
  ownerPrefix?: string,
  repositoryType?: string,
  repositoryPurpose?: string,
): string => {
  if (!repositoryName) return '';

  // Determine status emoji based on validation and completeness
  let statusEmoji = '';
  if (!validationResult.isValid) {
    statusEmoji = '❌ ';
  } else if (ownerPrefix && repositoryType && repositoryPurpose) {
    statusEmoji = '✅ ';
  } else {
    statusEmoji = '🔄 ';
  }

  return `${statusEmoji}${repositoryName}`;
};
